import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router';

import {Badge, Button, Icon, IconButton, LoadingSpinner, Text} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {ClassroomModelV1} from '@albert-io/models';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import ClassroomFlair from 'client/StudentHomepage/ClassroomFlair';
import NewFolderModal from 'components/PracticeView/FoldersModal/NewFolderModal/NewFolderModal.react';

import {useTeacherClassrooms} from '../hooks/useTeacherClassrooms';
import {useTeacherHomepageStore} from '../useTeacherHomepageStore';

import {CreateNewMenu} from './CreateNewMenu';

const HIDDEN_SCROLLBAR_CLASSES =
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

interface SidemenuNavItem {
  icon: IconProp;
  key: string;
  label: string;
  route: string;
}

const getItemClassNames = ({isSelected = false, hasIconGap = true} = {}) =>
  classNames(
    'flex grow items-center rounded-x1 px-x2 py-x1-half no-underline',
    hasIconGap && 'gap-x1',
    isSelected ? 'background-brand-subtle' : 'hover:background-brand-subtle'
  );

const ItemLabel = ({children}: {children: React.ReactNode}) => (
  <Text className='truncate' color='primary' size='s'>
    {children}
  </Text>
);

const ItemIcon = ({className, icon}: {className?: string; icon: IconProp}) => (
  <Icon
    className={classNames('text-m content-primary opacity-85', className)}
    fixedWidth
    icon={icon}
    iconStyle='regular'
  />
);

const ArchivedDisclosureIcon = ({isOpen}: {isOpen: boolean}) =>
  isOpen ? (
    <ItemIcon icon='chevron-down' />
  ) : (
    <>
      <ItemIcon className='group-hover:hidden' icon='archive' />
      <ItemIcon className='hidden group-hover:inline-block' icon='chevron-right' />
    </>
  );

const ClassroomRow = ({
  classroom,
  isArchived = false,
  pathname
}: {
  classroom: ClassroomModelV1;
  isArchived?: boolean;
  pathname: string;
}) => {
  const route = `/classes/${classroom.getId()}`;

  return (
    <li>
      <Link
        className={getItemClassNames({
          isSelected: pathname.startsWith(route),
          hasIconGap: isArchived
        })}
        // Bare /classes/:id has no index route; assignments is the class landing tab.
        to={`${route}/assignments`}
      >
        {isArchived ? (
          <ItemIcon icon='archive' />
        ) : (
          <ClassroomFlair isForAll={false} classroom={classroom} />
        )}
        <ItemLabel>{classroom.getName()}</ItemLabel>
      </Link>
    </li>
  );
};

const NAV_ITEMS: SidemenuNavItem[] = [
  {icon: 'home', key: 'home', label: 'Home', route: '/home'},
  {icon: 'chalkboard', key: 'classwork', label: 'All Classwork', route: '/assignments'}
];

const CollapsedClassroomRow = ({
  classroom,
  isArchived = false,
  pathname
}: {
  classroom: ClassroomModelV1;
  isArchived?: boolean;
  pathname: string;
}) => {
  const route = `/classes/${classroom.getId()}`;

  return (
    <li className='flex'>
      <Link
        aria-label={classroom.getName()}
        className={classNames(
          'flex grow items-center rounded-x1 py-x1-half pr-x1 pl-x2 no-underline',
          pathname.startsWith(route) ? 'background-brand-subtle' : 'hover:background-brand-subtle'
        )}
        to={`${route}/assignments`}
      >
        {isArchived ? (
          <Badge
            aria-hidden='true'
            className='mr-x1 border-primary background-primary font-bold! content-secondary [&>.a-text]:leading-none'
            roundness='circle'
            size='small'
          >
            {classroom.getName()?.charAt(0).toUpperCase() ?? ''}
          </Badge>
        ) : (
          <ClassroomFlair isForAll={false} classroom={classroom} />
        )}
      </Link>
    </li>
  );
};

interface Props {
  pathname: string;
}

export const Sidemenu = ({pathname}: Props) => {
  const isSidemenuCollapsed = useTeacherHomepageStore((state) => state.isSidemenuCollapsed);
  const setIsSidemenuCollapsed = useTeacherHomepageStore((state) => state.setIsSidemenuCollapsed);
  const [isNewClassModalOpen, setIsNewClassModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);

  const {
    archivedClassrooms,
    areArchivedClassroomsLoading,
    areClassroomsLoading,
    classrooms,
    fetchArchivedClassrooms,
    fetchClassrooms
  } = useTeacherClassrooms();

  const isShowingArchived = useTeacherHomepageStore((state) => state.isShowingArchivedClassrooms);
  const setIsShowingArchived = useTeacherHomepageStore(
    (state) => state.setIsShowingArchivedClassrooms
  );

  useEffect(() => {
    fetchClassrooms();
    if (isShowingArchived) {
      fetchArchivedClassrooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleArchived = () => {
    const willShow = !isShowingArchived;
    setIsShowingArchived(willShow);
    if (willShow && archivedClassrooms.isEmpty() && !areArchivedClassroomsLoading) {
      fetchArchivedClassrooms();
    }
  };

  const showActiveSpinner = areClassroomsLoading && classrooms.isEmpty();
  const showArchivedSpinner = areArchivedClassroomsLoading && archivedClassrooms.isEmpty();

  const modals = (
    <>
      {isNewClassModalOpen && (
        <NewClassroomModal
          handleClose={() => setIsNewClassModalOpen(false)}
          onSubmit={fetchClassrooms}
        />
      )}
      {isNewFolderModalOpen && (
        <NewFolderModal handleClose={() => setIsNewFolderModalOpen(false)} />
      )}
    </>
  );

  if (isSidemenuCollapsed) {
    return (
      <nav
        aria-label='Teacher dashboard'
        className={classNames(
          'flex h-full w-x9 shrink-0 flex-col gap-x3 overflow-x-hidden overflow-y-auto border-r border-primary px-x1 pt-x2 pb-x4',
          HIDDEN_SCROLLBAR_CLASSES
        )}
      >
        <div className='flex w-full flex-col items-center gap-x1'>
          <IconButton
            icon='sidebar'
            iconStyle='regular'
            label='Show side menu'
            size='s'
            onClick={() => setIsSidemenuCollapsed(false)}
          />
          <CreateNewMenu
            onNewClass={() => setIsNewClassModalOpen(true)}
            onNewFolder={() => setIsNewFolderModalOpen(true)}
            trigger={(triggerProps) => (
              <IconButton
                {...triggerProps}
                color='primary'
                icon='plus'
                label='Create New'
                variant='solid'
              />
            )}
          />
        </div>
        <ul className='m-x0 flex list-none flex-col gap-x0-half p-x0'>
          {NAV_ITEMS.map((item) => (
            <li className='flex' key={item.key}>
              <Link
                aria-label={item.label}
                className={getItemClassNames({isSelected: pathname.startsWith(item.route)})}
                to={item.route}
              >
                <ItemIcon icon={item.icon} />
              </Link>
            </li>
          ))}
        </ul>
        <ul
          className={classNames(
            'm-x0 flex list-none flex-col gap-x0-half overflow-x-hidden overflow-y-auto p-x0',
            HIDDEN_SCROLLBAR_CLASSES
          )}
        >
          {classrooms.map((classroom) => (
            <CollapsedClassroomRow
              classroom={classroom}
              key={classroom.getId()}
              pathname={pathname}
            />
          ))}
          <li className='flex'>
            {/* Raw button so it renders identically to the sibling Link rows */}
            <button
              aria-label={isShowingArchived ? 'Hide Archived' : 'Show Archived'}
              className={`${getItemClassNames()} group w-full cursor-pointer border-none bg-transparent text-left`}
              onClick={handleToggleArchived}
              type='button'
            >
              <ArchivedDisclosureIcon isOpen={isShowingArchived} />
            </button>
          </li>
          {isShowingArchived &&
            (showArchivedSpinner ? (
              <li className='flex justify-center py-x1'>
                <LoadingSpinner bottomMarginLevel={0} size={2} />
              </li>
            ) : (
              archivedClassrooms.map((classroom) => (
                <CollapsedClassroomRow
                  classroom={classroom}
                  isArchived
                  key={classroom.getId()}
                  pathname={pathname}
                />
              ))
            ))}
        </ul>
        {modals}
      </nav>
    );
  }

  return (
    <nav
      aria-label='Teacher dashboard'
      className='flex h-full w-[280px] shrink-0 flex-col gap-x3 overflow-y-auto border-r border-primary px-x2 pt-x2 pb-x4'
    >
      <div className='flex items-center justify-between gap-x1'>
        <CreateNewMenu
          onNewClass={() => setIsNewClassModalOpen(true)}
          onNewFolder={() => setIsNewFolderModalOpen(true)}
          trigger={(triggerProps) => (
            <Button {...triggerProps} roundness='pill'>
              Create New
              <Icon className='ml-x1' icon='caret-down' />
            </Button>
          )}
        />
        <IconButton
          icon='sidebar'
          iconStyle='regular'
          label='Hide side menu'
          size='s'
          onClick={() => setIsSidemenuCollapsed(true)}
        />
      </div>
      <ul className='m-x0 flex list-none flex-col gap-x0-half p-x0'>
        {NAV_ITEMS.map((item) => (
          <li className='flex items-center' key={item.key}>
            <Link
              className={getItemClassNames({isSelected: pathname.startsWith(item.route)})}
              to={item.route}
            >
              <ItemIcon icon={item.icon} />
              <ItemLabel>{item.label}</ItemLabel>
            </Link>
          </li>
        ))}
      </ul>
      <section aria-label='Classes' className='min-h-0 flex flex-col gap-x1-half'>
        <div className='flex items-center gap-x1 pr-x1 pl-x2'>
          <Text className='grow' color='secondary' size='s'>
            Classes
          </Text>
          <IconButton
            color='secondary'
            disabled
            icon='magnifying-glass'
            label='Search classes'
            size='s'
            variant='solid'
          />
        </div>
        {showActiveSpinner ? (
          <div className='flex justify-center py-x2'>
            <LoadingSpinner bottomMarginLevel={0} size={2} />
          </div>
        ) : (
          <ul className='m-x0 flex list-none flex-col gap-x0-half overflow-y-auto p-x0'>
            {classrooms.isEmpty() ? (
              <li className='rounded-x1 border border-dashed border-primary px-x2 py-x3 text-center'>
                <Text color='secondary' size='xs'>
                  Your classes will appear here
                </Text>
              </li>
            ) : (
              classrooms.map((classroom) => (
                <ClassroomRow classroom={classroom} key={classroom.getId()} pathname={pathname} />
              ))
            )}
            <li>
              {/* Raw button so it renders identically to the sibling Link rows */}
              <button
                className={`${getItemClassNames()} group w-full cursor-pointer border-none bg-transparent text-left`}
                onClick={handleToggleArchived}
                type='button'
              >
                <ArchivedDisclosureIcon isOpen={isShowingArchived} />
                <ItemLabel>{isShowingArchived ? 'Hide Archived' : 'Show Archived'}</ItemLabel>
              </button>
            </li>
            {isShowingArchived &&
              (showArchivedSpinner ? (
                <li className='flex justify-center py-x1'>
                  <LoadingSpinner bottomMarginLevel={0} size={2} />
                </li>
              ) : archivedClassrooms.isEmpty() ? (
                <li className='py-x1 pl-x2'>
                  <Text color='secondary' size='xs'>
                    No archived classes
                  </Text>
                </li>
              ) : (
                archivedClassrooms.map((classroom) => (
                  <ClassroomRow
                    classroom={classroom}
                    isArchived
                    key={classroom.getId()}
                    pathname={pathname}
                  />
                ))
              ))}
          </ul>
        )}
      </section>
      {modals}
    </nav>
  );
};
